import ReturnHeaderMeta from "@dana/Components/StandardReturns/ReturnHeaderMeta";

import EditIcon from "@dana/Icons/EditIcon";
import { ReturnOrderLineModel, WizardStepKey } from "@dana/Services/DanaStandardReturnOrderService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable from "@insite/mobius/DataTable";
import {
    Button,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
} from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// --- Props Interface ---
interface StepReviewProps {
    items: ReturnOrderLineModel[];
    internalRefNumber?: string;
    goTo: (s: WizardStepKey) => void;
}

// --- Styles Interfaces and Objects ---
export interface StepReviewStyles {
    editableGrid: InjectableCss;
    tableWrap: InjectableCss;
    editTitle?: TypographyPresentationProps;
    descText?: TypographyPresentationProps;
    editButton?: ButtonPresentationProps;
}

export const StepReviewStyles: StepReviewStyles = {
    editableGrid: {
        css: css`
            display: grid;
            grid-gap: 16px;
            grid-template-columns: 1fr;
            margin-top: 24px;

            @media only screen and (min-width: 767px) {
                grid-template-columns: repeat(3, 1fr);
                gap: 32px;
            }

            .emailContacts {
                ul {
                    margin: 8px 0;
                    padding-left: 0;
                    list-style: none;
                    word-break: break-word;

                    li {
                        &:not(:last-child) {
                            margin-bottom: 4px;
                        }
                    }
                }
            }

            .shipping-contact {
                address {
                    font-style: normal;
                }
            }
        `,
    },
    tableWrap: {
        css: css`
            overflow: auto;
        `,
    },
    editTitle: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    descText: {
        css: css`
            display: inline-block;
            margin-top: 8px;
        `,
    },
    editButton: {
        variant: "tertiary",
        icon: {
            src: EditIcon,
            position: "right",
        },
    },
};

const styles = StepReviewStyles;
const mapStateToProps = (state: ApplicationStateDana) => {
    return {
        standardReturnSate: state.standardReturnState,
    };
};
type Props = ReturnType<typeof mapStateToProps> &
    StepReviewProps;

// --- Component Definition ---
const StepReview: FC<Props> = ({
    standardReturnSate,
    internalRefNumber,
    goTo,
}) => {

    const displayReason = (reason?: string): string => {
        return standardReturnSate?.standardReturnReasons?.find(x => x.name === reason)?.description || reason || '';
    }

    const displayDisposition = (disposition?: string): string => {
        return standardReturnSate.dispositionPreferences?.find(x => x.name === disposition)?.description || disposition || '';
    }
    return (
        <StyledWrapper>
            <ReturnHeaderMeta />
            <StyledWrapper>
                <Typography variant="h3">{translate("Return Items")}</Typography>
                <StyledWrapper {...styles.tableWrap}>
                    <DataTable>
                        <DataTableHead>
                            <DataTableHeader>{translate("Line No")}</DataTableHeader>
                            <DataTableHeader>{translate("Part No")}</DataTableHeader>
                            <DataTableHeader>{translate("Customer Part No.")}</DataTableHeader>
                            <DataTableHeader>{translate("Description")}</DataTableHeader>
                            <DataTableHeader>{translate("Shipped Qty")}</DataTableHeader>
                            <DataTableHeader>{translate("Return Item Qty")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {standardReturnSate.selectedLines?.map(item => {
                                if (item.returnItemQty) {
                                    return (
                                        <DataTableRow key={item.lineNumber}>
                                            <DataTableCell>{item.lineNumber}</DataTableCell>
                                            <DataTableCell>{item.productErpNumber}</DataTableCell>
                                            <DataTableCell>{item.customerProductNumber}</DataTableCell>
                                            <DataTableCell>{item.description}</DataTableCell>
                                            <DataTableCell>{item.qtyShipped}</DataTableCell>
                                            <DataTableCell>{item.returnItemQty}</DataTableCell>
                                        </DataTableRow>
                                    );
                                }
                                return null; // Ensure a return value for all cases
                            })}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.editableGrid}>
                <StyledWrapper className="reason">
                    <StyledWrapper className="buttonWrapper">
                        <Typography className="font-semibold" {...styles.editTitle}>
                            {translate("Reason for Return")}
                        </Typography>
                        <Button
                            {...styles.editButton}
                            onClick={() => goTo("REASON_DISPOSITION")}
                            aria-label="Edit Reason & Disposition"
                        ></Button>
                    </StyledWrapper>
                    <Typography {...styles.descText}>{displayReason(standardReturnSate?.reason)}</Typography>
                </StyledWrapper>

                <StyledWrapper className="disposition-pref">
                    <StyledWrapper className="buttonWrapper">
                        <Typography className="font-semibold" {...styles.editTitle}>
                            {translate("Choose Disposition Preference")}
                        </Typography>
                        <Button
                            {...styles.editButton}
                            onClick={() => goTo("REASON_DISPOSITION")}
                            aria-label="Edit Reason & Disposition"
                        ></Button>
                    </StyledWrapper>
                    <Typography {...styles.descText}>{displayDisposition(standardReturnSate?.disposition)}</Typography>
                </StyledWrapper>

                <StyledWrapper className="emailContacts">
                    <StyledWrapper className="buttonWrapper">
                        <Typography className="font-semibold" {...styles.editTitle}>
                            {translate("Email Confirmation Contact")}
                        </Typography>
                        <Button
                            {...styles.editButton}
                            onClick={() => goTo("EMAIL_CONTACTS")}
                            aria-label="Edit Email Contacts"
                        ></Button>
                    </StyledWrapper>
                    {standardReturnSate?.emailContacts.length > 0 ? (
                        <ul>
                            {standardReturnSate?.emailContacts.map((e, i) => (
                                <li key={i}>{e}</li>
                            ))}
                        </ul>
                    ) : (
                        <div>-</div>
                    )}
                </StyledWrapper>
                {standardReturnSate?.disposition !== "SCRAP_AT_DANA_LOCATION" && (

                    <StyledWrapper className="shipping-contact">
                        <StyledWrapper className="buttonWrapper">
                            <Typography className="font-semibold" {...styles.editTitle}>
                                {translate("Shipping Contact")}
                            </Typography>
                            <Button
                                {...styles.editButton}
                                onClick={() => goTo("FREIGHT_COLLECT")}
                                aria-label="Edit Shipping Contact"
                            ></Button>
                        </StyledWrapper>
                        {standardReturnSate?.freightCollectInfo && (
                            <StyledWrapper>
                                <Typography {...styles.descText}>{standardReturnSate?.freightCollectInfo?.customerName}</Typography>
                                <br />
                                <address>
                                    {standardReturnSate?.freightCollectInfo?.address1}
                                    <br />
                                    {standardReturnSate?.freightCollectInfo?.address2}
                                    <br />
                                    {standardReturnSate?.freightCollectInfo?.state} {standardReturnSate?.freightCollectInfo?.country} {standardReturnSate?.freightCollectInfo.zipCode}
                                </address>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                )}


                <StyledWrapper className="internal-ref-no">
                    <StyledWrapper className="buttonWrapper">
                        <Typography className="font-semibold" {...styles.editTitle}>
                            {translate("Your Internal Return Reference Number (optional)")}
                        </Typography>
                        <Button
                            {...styles.editButton}
                            onClick={() => goTo("INTERNAL_REF_ONLY")}
                            aria-label="Edit Internal Reference"
                        ></Button>
                    </StyledWrapper>
                    <Typography {...styles.descText}>{internalRefNumber || "-"}</Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};


export default connect(mapStateToProps)(StepReview)